import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { InfoTooltip } from "../components/InfoTooltip";
import { StatusBadge } from "../components/StatusBadge";
import { fmtMoney } from "../lib/format";

export default function Quality() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/quality/monitoring")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="quality-page">
      <PageHeader
        title="Quality"
        description="Monitor suppliers and experiences that may need attention — low ratings, high cancellation rates, missing availability, or failed bookings."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <QualityCard
          title="Low rating suppliers"
          info="Suppliers with an average rating below 4.0 across published reviews — consider an operational review or improvement plan."
          items={data?.low_rating_suppliers || []}
          loading={loading}
          testId="quality-low-rating"
          renderRow={(s) => (
            <Link to={`/suppliers/${s.id}`} className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-muted/30">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                <div className="text-xs text-muted-foreground">{s.city}, {s.country}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm tabular-nums">{s.average_rating?.toFixed(2)}</span>
                <StatusBadge status="warning" label="Low rating" variant="warning" />
              </div>
            </Link>
          )}
        />

        <QualityCard
          title="High cancellation rate"
          info="Suppliers with a cancellation rate above 6%. Investigate cause: capacity issues, supplier overcommitment, or product-market fit."
          items={data?.high_cancellation_suppliers || []}
          loading={loading}
          testId="quality-high-cancel"
          renderRow={(s) => (
            <Link to={`/suppliers/${s.id}`} className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-muted/30">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                <div className="text-xs text-muted-foreground">{s.city}, {s.country}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm tabular-nums">{s.cancellation_rate}%</span>
                <StatusBadge status="danger" label="High cancel" variant="danger" />
              </div>
            </Link>
          )}
        />

        <QualityCard
          title="Experiences missing availability"
          info="Published experiences that currently have zero open availability slots. Bookings cannot be created until slots are configured."
          items={data?.experiences_missing_availability || []}
          loading={loading}
          testId="quality-missing-avail"
          renderRow={(e) => (
            <Link to={`/experiences/${e.id}`} className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-muted/30">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{e.title}</div>
                <div className="text-xs text-muted-foreground">{e.destination} · {e.supplier_name}</div>
              </div>
              <StatusBadge status="warning" label="No slots" variant="warning" />
            </Link>
          )}
        />

        <QualityCard
          title="Failed bookings"
          info="Bookings whose payment or confirmation failed. Review for systemic issues — payment provider errors, capacity conflicts, etc."
          items={data?.failed_bookings || []}
          loading={loading}
          testId="quality-failed-bookings"
          renderRow={(b) => (
            <Link to={`/bookings/${b.id}`} className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-muted/30">
              <div className="min-w-0">
                <div className="text-sm font-medium font-mono">{b.reference}</div>
                <div className="text-xs text-muted-foreground truncate">{b.customer_name} · {b.experience_title}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs tabular-nums">{fmtMoney(b.amount_paid, b.currency)}</span>
                <StatusBadge status="failed" />
              </div>
            </Link>
          )}
        />
      </div>
    </div>
  );
}

function QualityCard({ title, info, items, loading, renderRow, testId }) {
  return (
    <div className="bg-card border border-border rounded-lg" data-testid={testId}>
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{title}</h3>
          <InfoTooltip text={info} />
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">{loading ? "…" : items.length}</span>
      </div>
      <div className="divide-y divide-border">
        {loading && <div className="px-5 py-10 text-center text-xs text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && <div className="px-5 py-10 text-center text-xs text-muted-foreground">All clear.</div>}
        {!loading && items.slice(0, 8).map((item) => (
          <div key={item.id}>{renderRow(item)}</div>
        ))}
      </div>
    </div>
  );
}
